import { useEffect, memo, useMemo, useRef, useState } from 'react';
import { Avatar, Box, List, ListItemButton, ListItemIcon, ListItemText, Paper, Skeleton, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import { useLocales } from 'src/locales';
import countriesJson from 'src/utils/countries.json';
import Iconify from 'src/components/iconify';
import { DomainItem, AdType } from '../types';
import { domainFavicon } from '../utils';
import { normalizeDomain } from '../utils/domain';
import HoverShortcut from './HoverShortcut';
import CustomTooltip from 'src/components/visibility/components/CustomTooltip';

interface Props {
    items: DomainItem[];
    selected: string;
    onSelect: (domain: string) => void;
    adType: AdType;
    selectedCountry?: string | null;
    loading?: boolean;
}

const COMPETITOR_ROW_HEIGHT = 40;
const COMPETITOR_OVERSCAN = 6;

function LeftDomainsBase({ items, selected, onSelect, selectedCountry, loading }: Props) {
    const workspace: any = useCurrentWorkspace();
    const { t } = useLocales();
    const navigate = useNavigate();
    const [search, setSearch] = useState('');
    const competitorsContainerRef = useRef<HTMLDivElement>(null);
    const [competitorsScrollTop, setCompetitorsScrollTop] = useState(0);
    const [competitorsContainerHeight, setCompetitorsContainerHeight] = useState(0);

    const friendlyDomainNames: string[] = Array.from(new Set([
        ...(Array.isArray(workspace?.friendlyDomains) ? (workspace.friendlyDomains as string[]).filter(Boolean) : []),
        ...(workspace?.url ? [workspace.url] : [])
    ]
        .map((d: string) => normalizeDomain(d))
        .filter(Boolean)));

    const isFriendly = (domain: string) => friendlyDomainNames.includes(normalizeDomain(domain));

    const yourDomains = useMemo(
        () => items.filter((it) => isFriendly(it.domain)),
        [items, friendlyDomainNames]
    );

    const competitorDomains = useMemo(
        () => items.filter((it) => !isFriendly(it.domain)),
        [items, friendlyDomainNames]
    );

    const filteredCompetitors = useMemo(() => {
        const normalizedSearch = search.trim().toLowerCase();

        if (!normalizedSearch) return competitorDomains;

        return competitorDomains.filter((it) =>
            it.domain.toLowerCase().includes(normalizedSearch)
        );
    }, [competitorDomains, search]);

    useEffect(() => {
        setCompetitorsScrollTop(0);

        if (competitorsContainerRef.current) {
            competitorsContainerRef.current.scrollTop = 0;
        }
    }, [search, items]);

    useEffect(() => {
        const container = competitorsContainerRef.current;
        if (!container) return undefined;

        const updateHeight = () => {
            setCompetitorsContainerHeight(container.clientHeight);
        };

        updateHeight();

        const resizeObserver = new ResizeObserver(updateHeight);
        resizeObserver.observe(container);

        return () => resizeObserver.disconnect();
    }, []);

    const visibleCompetitorRange = useMemo(() => {
        const visibleCount = Math.ceil(
            competitorsContainerHeight / COMPETITOR_ROW_HEIGHT
        );

        const start = Math.max(
            0,
            Math.floor(competitorsScrollTop / COMPETITOR_ROW_HEIGHT) -
            COMPETITOR_OVERSCAN
        );

        const end = Math.min(
            filteredCompetitors.length,
            start + visibleCount + COMPETITOR_OVERSCAN * 2
        );

        return {
            start,
            items: filteredCompetitors.slice(start, end),
        };
    }, [
        competitorsContainerHeight,
        competitorsScrollTop,
        filteredCompetitors,
    ]);

    const firstItemDomain = (yourDomains[0]?.domain || competitorDomains[0]?.domain || '').toString();
    useEffect(() => {
        if (!selected && firstItemDomain) {
            onSelect(firstItemDomain);
        }
    }, [selected, firstItemDomain, onSelect]);

    const handleEvolutionClick = (e: React.MouseEvent, domain: string) => {
        e.stopPropagation();
        const countryEntry = countriesJson?.find((c) => c.name === selectedCountry);
        const code = countryEntry?.code?.toUpperCase() || '';
        navigate(`/brand/visibility/competitors?country=${encodeURIComponent(code)}&metric=paid&domain=${encodeURIComponent(domain)}`);
    };

    const hoverLabel = (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
            {t('dashboard.global.visibility.seeEvolution')}
            <Iconify icon="eva:diagonal-arrow-right-up-fill" width={14} />
        </Box>
    );

    const formatPaidPresenceRate = (rate: number) => (rate > 0 && rate < 1 ? '<1%' : `${rate.toFixed(0)}%`);

    const SkeletonRow = ({ keyId }: { keyId: number }) => (
        <ListItemButton
            key={keyId}
            sx={{
                height: 40,
                py: '10px',
                borderRadius: 1,
            }}
        >
            <ListItemIcon sx={{ minWidth: 28 }}>
                <Skeleton variant="circular" width={18} height={18} />
            </ListItemIcon>

            <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="75%" height={18} />
            </Box>

            <Skeleton variant="text" width={34} height={18} />
        </ListItemButton>
    );

    const DomainRow = ({ it }: { it: DomainItem }) => {
        const [rowHovered, setRowHovered] = useState(false);

        const paidPresenceRate = it.paidPresenceRate ?? 0;
        const paidPresenceRateCompare = it.paidPresenceRateCompare;

        const differencePp =
            paidPresenceRateCompare !== null &&
                paidPresenceRateCompare !== undefined
                ? paidPresenceRate - paidPresenceRateCompare
                : null;

        const formatDifferencePp = (value: number) => {
            const roundedValue = Math.round(value * 10) / 10;
            const prefix = roundedValue > 0 ? '+' : '';

            return `${prefix}${roundedValue.toFixed(0)}pp`;
        };

        return (
            <ListItemButton
                onMouseEnter={() => setRowHovered(true)}
                onMouseLeave={() => setRowHovered(false)}
                onClick={() => onSelect(it.domain)}
                selected={selected === it.domain}
                sx={{
                    position: 'relative',
                    height: "40px",
                    py: "10px",
                    borderRadius: 1,
                    overflow: 'hidden',
                    '&.Mui-selected': { bgcolor: 'action.selected' },
                    '&.Mui-selected:hover': { bgcolor: 'action.selected' },
                }}
            >
                <ListItemIcon sx={{ minWidth: 28 }}>
                    <Avatar
                        src={domainFavicon(it.domain)}
                        sx={{ width: 18, height: 18, backgroundColor: '#fff', transition: 'opacity 0.3s ease-in-out' }}
                    />
                </ListItemIcon>
                <ListItemText
                    sx={{
                        minWidth: 0,
                        mr: '8px',
                    }}
                    primary={
                        <Typography
                            variant="body2"
                            title={it.domain}
                            sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {it.domain}
                        </Typography>
                    }
                />

                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ flexShrink: 0 }}
                >
                    <Typography
                        variant="body2"
                        sx={{
                            minWidth: 34,
                            textAlign: 'right',
                            color: '#637381',
                            fontVariantNumeric: 'lining-nums tabular-nums',
                            fontFamily: 'Public Sans',
                            fontSize: '14px',
                            fontStyle: 'normal',
                            fontWeight: 400,
                            lineHeight: '100%',
                        }}
                    >
                        {formatPaidPresenceRate(paidPresenceRate)}
                    </Typography>

                    {differencePp !== null && (
                        <Typography
                            variant="body2"
                            sx={{
                                minWidth: 48,
                                textAlign: 'right',
                                color:
                                    differencePp > 0
                                        ? '#18A155'
                                        : differencePp < 0
                                            ? '#DC1818'
                                            : '#637381',
                                fontVariantNumeric: 'lining-nums tabular-nums ordinal',
                                fontFamily: 'Public Sans',
                                fontSize: '13px',
                                fontStyle: 'normal',
                                lineHeight: '100%',
                            }}
                        >
                            {formatDifferencePp(differencePp)}
                        </Typography>
                    )}
                </Stack>

                {rowHovered && (
                    <Box
                        sx={{
                            position: 'absolute',
                            right: 0,
                            top: 0,
                            bottom: 0,
                            display: 'flex',
                            alignItems: 'center',
                            pl: 10,
                            pr: 1,
                            background: 'linear-gradient(to right, rgba(255,255,255,0) 0%, rgba(255,255,255,0.85) 40%)',
                        }}
                    >
                        <HoverShortcut
                            defaultContent=""
                            hoverContent={hoverLabel}
                            onClick={(e) => handleEvolutionClick(e, it.domain)}
                            forceHovered
                            fontSize={14}
                            sx={{ ml: 0 }}
                        />
                    </Box>
                )}
            </ListItemButton>
        );
    };

    const renderRow = (it: DomainItem) => <DomainRow key={it.domain} it={it} />;

    return (
        <Paper
            sx={{
                mt: 3,
                backgroundColor: 'transparent',
                borderRadius: 1,
                height: '100%',
                px: "16px",
                display: 'flex',
                flexDirection: 'column',
                overflow: 'auto',
            }}
        >
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                <Typography
                    variant="overline"
                    sx={{ color: 'text.secondary', fontSize: 12, fontWeight: 400 }}>
                    {t("dashboard.global.visibility.your_domains")}
                </Typography>
                <Stack direction="row" gap={"2px"}>
                    <CustomTooltip title={t("dashboard.global.visibility.paidPresenceRate")}>
                        <Stack display={"flex"} direction={"row"} gap={"2px"}>
                            <Typography
                                variant="body2"
                                sx={{
                                    cursor: 'help',
                                    color: '#637381',
                                    fontFamily: 'Public Sans',
                                    fontSize: '12px',
                                    fontStyle: 'normal',
                                    fontWeight: 500,
                                    lineHeight: '100%',
                                }}
                            >
                                Paid Presence Rate
                            </Typography>
                            <svg style={{ cursor: "help" }} xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                <g clip-path="url(#clip0_6211_9170)">
                                    <path d="M6 8V6M6 4H6.005M11 6C11 8.76142 8.76142 11 6 11C3.23858 11 1 8.76142 1 6C1 3.23858 3.23858 1 6 1C8.76142 1 11 3.23858 11 6Z" stroke="#637381" stroke-linecap="round" stroke-linejoin="round" />
                                </g>
                                <defs>
                                    <clipPath id="clip0_6211_9170">
                                        <rect width="12" height="12" fill="white" />
                                    </clipPath>
                                </defs>
                            </svg>
                        </Stack>
                    </CustomTooltip>
                </Stack>
            </Stack>
            <List dense disablePadding sx={{ mb: 1, mt: 1 }}>
                {loading
                    ? Array.from({ length: Math.max(yourDomains.length, 2) }).map((_, i) => (
                        <SkeletonRow key={i} keyId={i} />
                    ))
                    : yourDomains.map(renderRow)}
            </List>

            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ pt: 1.5, mb: 1, mt: yourDomains.length > 0 ? 0.5 : 0 }}>
                <Typography
                    variant="overline"
                    sx={{ color: 'text.secondary', fontSize: 12, fontWeight: 400 }}>
                    {t("dashboard.global.visibility.competitors")}
                </Typography>
            </Stack>

            <TextField
                size="small"
                disabled={loading}
                fullWidth
                placeholder="Search competitors..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{
                    mb: 1.5,
                    '& .MuiOutlinedInput-root': {
                        height: 36,
                        borderRadius: '8px',
                        backgroundColor: '#F4F6F8',
                    },
                    '& .MuiInputBase-input': {
                        fontSize: 14,
                    },
                }}
            />

            <Box
                ref={competitorsContainerRef}
                onScroll={(event) => {
                    setCompetitorsScrollTop(event.currentTarget.scrollTop);
                }}
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    position: 'relative',
                }}
            >
                {loading ? (
                    <List dense disablePadding>
                        {Array.from({ length: 14 }).map((_, i) => (
                            <SkeletonRow key={i} keyId={i} />
                        ))}
                    </List>
                ) : (
                    <Box
                        sx={{
                            position: 'relative',
                            height:
                                filteredCompetitors.length *
                                COMPETITOR_ROW_HEIGHT,
                        }}
                    >
                        <Box
                            sx={{
                                position: 'absolute',
                                top:
                                    visibleCompetitorRange.start *
                                    COMPETITOR_ROW_HEIGHT,
                                left: 0,
                                right: 0,
                            }}
                        >
                            {visibleCompetitorRange.items.map(renderRow)}
                        </Box>
                    </Box>
                )}
            </Box>
        </Paper>
    );
}

const LeftDomains = memo(LeftDomainsBase, (prevProps, nextProps) =>
    prevProps.items === nextProps.items &&
    prevProps.selected === nextProps.selected &&
    prevProps.adType === nextProps.adType &&
    prevProps.loading === nextProps.loading &&
    prevProps.selectedCountry === nextProps.selectedCountry
);

export default LeftDomains;